import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { navigate } from 'raviger';
import { Formik } from 'formik';
import { Form as BootstrapForm, Button, ButtonGroup, Col, OverlayTrigger, Tooltip, Row, Card, Modal } from "react-bootstrap";
import { AddressSearch, TextInput, ToggleSwitch } from '../components/Form';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import ButtonSpinner from '../components/ButtonSpinner';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';

// Regex validators.
const nameRegex = /^[a-z0-9 ,.'-]+$/i;

const ShelterForm = ({ id, incident=null, organization }) => {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const initial_coordinates = state?.incident?.coordinates ?? [0, 0];

  // Initial shelter data.
  const [data, setData] = useState({
    name: '',
    description: '',
    phone: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zip_code: '',
    incident: state.incident.id,
    training: false,
    operational: false,
    organization: state.organization.id,
    latitude: null,
    longitude: null,
  });

  // Regex validators.
  const phoneRegex = /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;
  const shelterApiUrl = incident ? 
    '/shelter/api/shelter/' + id + '/?incident=' + incident + '&organization=' + organization
  : '/shelter/api/shelter/' + id + '/?organization=' + organization

  // Track duplicate shelter name error.
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    if (id) {
      const fetchShelterData = async () => {
        // Fetch Shelter data.
        await axios.get(shelterApiUrl, {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            // Set phone field to be the pretty version.
            response.data['phone'] = response.data['display_phone']
            setData(response.data);
          }
        })
        .catch(error => {
          if (!unmounted) {
            setShowSystemError(true);
          }
        });
      };
      fetchShelterData();
    }
    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, incident]);

  return (
    <>
      <Formik
        initialValues={data}
        enableReinitialize={true}
        validationSchema={Yup.object({
          name: Yup.string()
            .max(50, 'Must be 50 characters or less')
            .matches(nameRegex, "Name is not valid")
            .required('Required'),
          description: Yup.string()
            .max(400, 'Must be 400 characters or less'),
          phone: Yup.string()
            .matches(phoneRegex, "Phone number is not valid"),
          address: Yup.string()
            .required('Required'),
          apartment: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          city: Yup.string(),
          state: Yup.string(),
          zip_code: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          latitude: Yup.number()
            .nullable(),
          longitude: Yup.number()
            .nullable(),
          training: Yup.boolean(),
          operational: Yup.boolean()
        })}
        onSubmit={(values, { setSubmitting }) => {
          if (id) {
            axios.put(shelterApiUrl, values)
            .then(function() {
                navigate('/' + organization + "/manage/shelters");
            })
            .catch(error => {
              if (error.response.data && error.response.data.name && error.response.data.name[0].includes('shelter with this name already exists')) {
                setShow(true);
              }
              else {
                setShowSystemError(true);
              }
            });
          }
          else {
            axios.post('/shelter/api/shelter/', values)
            .then(response => {
              navigate('/' + organization + "/manage/shelters");
            })
            .catch(error => {
              if (error.response.data && error.response.data.name && error.response.data.name[0].includes('shelter with this name already exists')) {
                setShow(true);
              }
              else {
                setShowSystemError(true);
              }
            });
            setSubmitting(false);
          }
        }}
      >
        {props => (
          <>
          <Card border="secondary" className="mt-5 ms-auto me-auto" style={{width:"60%", maxWidth:"60%"}}>
            <Card.Header as="h5" className="ps-3"><span style={{cursor:'pointer'}} onClick={() => window.history.back()} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>{!id ? "New" : "Update"} Shelter</Card.Header>
            <Card.Body>
              <BootstrapForm noValidate>
                <Row>
                  <TextInput
                    xs="12"
                    type="text"
                    label="Name*"
                    name="name"
                    id="name"
                  />
                </Row>
                <Row className="mt-3">
                  <TextInput
                    xs="12"
                    type="text"
                    label="Phone"
                    name="phone"
                  />
                </Row>
                <Row className="mt-3 mb-3">
                  <TextInput
                    as="textarea"
                    rows={5}
                    xs="12"
                    type="text"
                    label="Description"
                    name="description"
                  />
                </Row>
                <AddressSearch formikProps={props} label="Search for Shelter Address" show_apt={false} incident={incident} always_show={true} initial_coordinates={initial_coordinates} error="Shelter Address was not selected." />
                {incident ? "": 
                <Row className="mt-3">
                  <ToggleSwitch
                    xs={4}
                    name="operational"
                    id="operational"
                    label={
                      <span>
                        Operational Shelter
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-active-incidents">
                              Enabling this setting will make the shelter available in all operational incidents.
                              Disabling it will remove the shelter from all new operational incidents, but it will remain in any incidents that already have animals assigned to it.
                              <br />
                              Active Op. Incidents:
                              <br />
                              {Object.keys(data.active_operational_incidents || {}).length > 0 ? (
                                Object.entries(data.active_operational_incidents).map(
                                  ([inc_slug, incident]) => (
                                    <span key={inc_slug}>
                                      {incident.name} ({incident.animal_count})
                                      <br />
                                    </span>
                                  )
                                )
                              ) : (
                                <span>None</span>
                              )}
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              className="ms-1"
                              size="sm"
                            />
                          </OverlayTrigger>
                      </span>
                    }
                  />
                  <ToggleSwitch
                    xs={4}
                    name="training"
                    id="training"
                    label={
                      <span>
                        Training Shelter
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-active-incidents">
                              Enabling this setting will make the shelter available in all training incidents.
                              Disabling it will remove the shelter from all new training incidents, but it will remain in any incidents that already have animals assigned to it.
                              <br />
                              Active Training Incidents:
                              <br />
                              {Object.keys(data.active_training_incidents || {}).length > 0 ? (
                                Object.entries(data.active_training_incidents).map(
                                  ([inc_slug, incident]) => (
                                    <span key={inc_slug}>
                                      {incident.name} ({incident.animal_count})
                                      <br />
                                    </span>
                                  )
                                )
                              ) : (
                                <span>None</span>
                              )}
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              className="ms-1"
                              size="sm"
                            />
                          </OverlayTrigger>
                      </span>
                    }
                />
                </Row>
                }
              </BootstrapForm>
            </Card.Body>
            <ButtonGroup size="lg">
              <ButtonSpinner isSubmitting={props.isSubmitting} isSubmittingText="Saving..." type="submit" onClick={() => { props.submitForm()}}>Save</ButtonSpinner>
            </ButtonGroup>
          </Card>
          <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
              <Modal.Title>Duplicate Shelter Name Found</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                A shelter with the name {props.values.name} already exists. Please enter a unique name.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>Close</Button>
            </Modal.Footer>
          </Modal>
        </>
        )}
      </Formik>
    </>
  );
};

export default ShelterForm;
